import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Zap, Plus, Play, Trash2, ArrowLeft, Loader2, Repeat,
} from "lucide-react";
import { PageHead, SectionHead } from "@/components/SectionHead";
import { EmptyState } from "@/components/EmptyState";
import { format } from "date-fns";

const emptyFilter = { q: "", state: "", city: "", type: "", business_type: "", has_email: "", has_gst: "" };
const emptyForm = {
  name: "", enabled: true, target_company_id: "", assigned_to: "",
  contact_type_override: "", interval_hours: 24, batch_size: 100,
  filter: { ...emptyFilter },
};

function RuleEditor({ open, onOpenChange, initial, onSaved }) {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(initial || emptyForm);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(initial || emptyForm); }, [initial, open]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/super-admin/overview");
        setCompanies((data.companies || []).filter((c) => c.code !== "SUPER"));
      } catch (_) { /* silent */ }
    })();
  }, [open]);

  useEffect(() => {
    if (!form.target_company_id) { setUsers([]); return; }
    (async () => {
      try {
        await api.post("/super-admin/switch-tenant", { company_id: form.target_company_id });
        const { data } = await api.get("/users");
        setUsers(data || []);
      } catch (_) { setUsers([]); }
    })();
  }, [form.target_company_id]);

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setFilter = (patch) => setForm((f) => ({ ...f, filter: { ...f.filter, ...patch } }));

  const submit = async () => {
    if (!form.name || !form.target_company_id) return toast.error("Name and target company are required");
    setBusy(true);
    try {
      // Coerce empty strings → nulls; has_email/has_gst → boolean
      const filter = {
        ...form.filter,
        has_email: form.filter.has_email === "" ? null : form.filter.has_email === "yes",
        has_gst: form.filter.has_gst === "" ? null : form.filter.has_gst === "yes",
      };
      Object.keys(filter).forEach((k) => { if (filter[k] === "" || filter[k] === null) delete filter[k]; });
      const payload = {
        name: form.name,
        enabled: form.enabled,
        target_company_id: form.target_company_id,
        assigned_to: form.assigned_to || null,
        contact_type_override: form.contact_type_override || null,
        interval_hours: Number(form.interval_hours) || 24,
        batch_size: Number(form.batch_size) || 100,
        filter,
      };
      if (initial?.id) {
        await api.patch(`/super-admin/promotion-rules/${initial.id}`, payload);
      } else {
        await api.post("/super-admin/promotion-rules", payload);
      }
      toast.success("Rule saved");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="rule-editor">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{initial?.id ? "Edit rule" : "New auto-promote rule"}</DialogTitle>
          <DialogDescription>
            Every N hours, promote master contacts matching the filter into a company&apos;s CRM.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setF({ name: e.target.value })} placeholder="e.g. Gujarat Wholesalers weekly" className="mt-1 h-11" data-testid="rule-name-input"/>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <Switch checked={!!form.enabled} onCheckedChange={(v) => setF({ enabled: !!v })} data-testid="rule-enabled-switch"/>
              <span className="text-sm">Enabled — scheduler runs this rule</span>
            </div>
          </div>

          <div className="rounded-xl border border-[#E4DFCF] p-4 bg-[#F6F1E1]/40">
            <div className="text-[10px] uppercase tracking-widest text-[#C9A24B] mb-2 font-medium">Segment filter</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><Label>State</Label><Input value={form.filter.state} onChange={(e)=>setFilter({state:e.target.value})} placeholder="Gujarat" className="mt-1 h-10"/></div>
              <div><Label>City</Label><Input value={form.filter.city} onChange={(e)=>setFilter({city:e.target.value})} placeholder="Surat" className="mt-1 h-10"/></div>
              <div><Label>Type</Label>
                <Select value={form.filter.type} onValueChange={(v)=>setFilter({type:v})}>
                  <SelectTrigger className="mt-1 h-10"><SelectValue placeholder="Any"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Customer">Customer</SelectItem>
                    <SelectItem value="Lead">Lead</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Business</Label><Input value={form.filter.business_type} onChange={(e)=>setFilter({business_type:e.target.value})} placeholder="Wholesaler" className="mt-1 h-10"/></div>
              <div><Label>Has email</Label>
                <Select value={form.filter.has_email} onValueChange={(v)=>setFilter({has_email:v})}>
                  <SelectTrigger className="mt-1 h-10"><SelectValue placeholder="Any"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Has GST</Label>
                <Select value={form.filter.has_gst} onValueChange={(v)=>setFilter({has_gst:v})}>
                  <SelectTrigger className="mt-1 h-10"><SelectValue placeholder="Any"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Free text search</Label><Input value={form.filter.q} onChange={(e)=>setFilter({q:e.target.value})} placeholder="Optional — name / mobile / email fragment" className="mt-1 h-10"/></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Target company</Label>
              <Select value={form.target_company_id} onValueChange={(v)=>setF({target_company_id:v})}>
                <SelectTrigger className="mt-1 h-11" data-testid="rule-company-select"><SelectValue placeholder="Choose tenant"/></SelectTrigger>
                <SelectContent>
                  {companies.map((c)=><SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assign to salesperson (optional)</Label>
              <Select value={form.assigned_to} onValueChange={(v)=>setF({assigned_to:v})}>
                <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Unassigned"/></SelectTrigger>
                <SelectContent>
                  {users.filter((u)=>["sales","order_team","manager","admin"].includes(u.role))
                    .map((u)=><SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Interval</Label>
              <Select value={String(form.interval_hours)} onValueChange={(v)=>setF({interval_hours:Number(v)})}>
                <SelectTrigger className="mt-1 h-11"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Hourly</SelectItem>
                  <SelectItem value="6">Every 6 hours</SelectItem>
                  <SelectItem value="12">Every 12 hours</SelectItem>
                  <SelectItem value="24">Daily</SelectItem>
                  <SelectItem value="168">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Batch size per run</Label>
              <Input type="number" value={form.batch_size} onChange={(e)=>setF({batch_size:e.target.value})} min={1} max={1000} className="mt-1 h-11"/>
            </div>
            <div className="md:col-span-2">
              <Label>Contact type override (optional)</Label>
              <Select value={form.contact_type_override} onValueChange={(v)=>setF({contact_type_override:v})}>
                <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Keep source type"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={submit} disabled={busy} data-testid="rule-save-btn">
            {busy ? <Loader2 className="animate-spin mr-2" size={14}/> : <Zap size={14} className="mr-1"/>}
            {initial?.id ? "Save changes" : "Create rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PromotionRulesPage() {
  const nav = useNavigate();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/promotion-rules");
      setRules(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runNow = async (r) => {
    setBusyId(r.id);
    try {
      const { data } = await api.post(`/super-admin/promotion-rules/${r.id}/run-now`);
      toast.success(`Promoted ${data.promoted} · ${data.skipped_duplicates} duplicates skipped`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    try {
      await api.delete(`/super-admin/promotion-rules/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggle = async (r, enabled) => {
    try {
      await api.patch(`/super-admin/promotion-rules/${r.id}`, { enabled });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEdit = (r) => {
    const filter = { ...emptyFilter, ...(r.filter || {}) };
    filter.has_email = filter.has_email === true ? "yes" : filter.has_email === false ? "no" : "";
    filter.has_gst = filter.has_gst === true ? "yes" : filter.has_gst === false ? "no" : "";
    setEditing({
      id: r.id, name: r.name, enabled: r.enabled,
      target_company_id: r.target_company_id, assigned_to: r.assigned_to || "",
      contact_type_override: r.contact_type_override || "",
      interval_hours: r.interval_hours || 24, batch_size: r.batch_size || 100,
      filter,
    });
    setShowEditor(true);
  };

  const intervalLabel = (h) => ({1:"Hourly", 6:"6-hourly", 12:"12-hourly", 24:"Daily", 168:"Weekly"}[h] || `${h}h`);

  return (
    <div className="space-y-6" data-testid="promotion-rules-page">
      <PageHead
        eyebrow="TARU Platform · Automation"
        title="Auto-Promote Rules"
        subtitle="Schedule cross-tenant lead promotions. Runs quietly in the background."
        actions={
          <>
            <Button variant="outline" className="rounded-full h-11" onClick={() => nav("/super-admin")}><ArrowLeft size={14} className="mr-1"/>Back</Button>
            <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white h-11 px-6" onClick={() => { setEditing(null); setShowEditor(true); }} data-testid="new-rule-btn">
              <Plus size={16} className="mr-1"/> New rule
            </Button>
          </>
        }
      />

      {rules.length === 0 && !loading ? (
        <EmptyState
          icon={Repeat}
          eyebrow="No automations yet"
          title="Turn the master pool into an engine"
          description="Create a rule to auto-promote a specific segment (e.g. Gujarat wholesalers with GST) into a company's CRM on your chosen cadence."
          action={{ label: "Create first rule", onClick: () => { setEditing(null); setShowEditor(true); }, testId: "empty-new-rule" }}
        />
      ) : (
        <Card className="border-[#E4DFCF] rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F6F1E1]/60 text-[10px] uppercase tracking-widest text-[#4A5A50]">
                  <th className="p-3 text-left">Rule</th>
                  <th className="p-3 text-left">Segment</th>
                  <th className="p-3 text-left">Target</th>
                  <th className="p-3 text-left">Cadence</th>
                  <th className="p-3 text-left">Last run</th>
                  <th className="p-3 text-left">Next run</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => {
                  const f = r.filter || {};
                  const segParts = [f.state, f.city, f.type, f.business_type, f.has_email && "has email", f.has_gst && "has GST"].filter(Boolean);
                  return (
                    <tr key={r.id} className="border-t border-[#E4DFCF] hover:bg-[#F6F1E1]/40">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Switch checked={!!r.enabled} onCheckedChange={(v)=>toggle(r,v)}/>
                          <span className="font-medium">{r.name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-xs text-[#4A5A50]">{segParts.length ? segParts.join(" · ") : "All contacts"}</td>
                      <td className="p-3 text-xs">{r.target_company_id?.slice(0,8)}…{r.assigned_to ? " · assigned" : ""}</td>
                      <td className="p-3 text-xs">{intervalLabel(r.interval_hours)} · {r.batch_size}/run</td>
                      <td className="p-3 text-xs">
                        {r.last_run_at ? (
                          <div>
                            <div>{format(new Date(r.last_run_at), "d MMM HH:mm")}</div>
                            <div className="text-[#0F5E39]">{r.last_promoted} new · {r.last_skipped_duplicates} dup</div>
                          </div>
                        ) : <span className="text-[#4A5A50]">Never</span>}
                      </td>
                      <td className="p-3 text-xs">{r.next_run_at ? format(new Date(r.next_run_at), "d MMM HH:mm") : "—"}</td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => runNow(r)} disabled={busyId === r.id} data-testid={`run-now-${r.id}`}>
                            {busyId === r.id ? <Loader2 size={14} className="animate-spin"/> : <Play size={14}/>}
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(r)}>Edit</Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(r)}><Trash2 size={14}/></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <RuleEditor open={showEditor} onOpenChange={setShowEditor} initial={editing} onSaved={load}/>
    </div>
  );
}
